import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Clock, ChevronRight, ArrowRight, Calendar, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const events = [
  {
    date: '13.02.2026',
    title: 'Jahreshauptversammlung 2026',
    desc: 'Ein vollbesetztes Vereinsheim, stolze Rückblicke und spannende Pläne für die Zukunft: Die JHV 2026 war ein voller Erfolg. Sieben Mitglieder wurden für 50-jährige Vereinstreue geehrt.',
    category: 'Verein',
    highlight: true,
  },
  {
    date: '15.08.2025',
    title: 'Mini-Kicker starten durch!',
    desc: 'Nach langer Pause sind die Mini-Kicker zurück! Fußball für Kinder von 2–6 Jahren, jeden Montag ab 16:30 Uhr. Der Start-Tag war ein riesiger Erfolg mit anschließendem Grillen.',
    category: 'Jugend',
    highlight: false,
  },
  {
    date: '16.03.2025',
    title: '5. Vereinsfrühstück',
    desc: 'Gemeinsam frühstücken, lachen und den Vereinsgeist stärken. Das Vereinsfrühstück ist eine Tradition, die uns alle zusammenbringt.',
    category: 'Fest',
    highlight: false,
  },
  {
    date: '23.06.2024',
    title: 'Lübbecke on tour – Dorfrallye',
    desc: 'Zum 800-jährigen Dorfjubiläum organisierten wir gemeinsam mit der Dorfgemeinschaft eine spannende Rallye mit 19 Stationen durch ganz Alswede.',
    category: 'Event',
    highlight: false,
  },
  {
    date: '2025',
    title: 'Das Jahrhundertspiel – die Neuauflage',
    desc: 'Vor 20 Jahren spielten wir gegen Werder Bremen. Jetzt kamen die Werder-Legenden zurück! Ein unvergessliches Fußballfest mit Rahmenprogramm, CL-Finale auf Leinwand und Party bis in die Nacht.',
    category: 'Highlight',
    highlight: true,
  },
  {
    date: '2024',
    title: '1. HSC Darts Open',
    desc: 'Darts boomt beim HSC! Das erste Darts-Open war ein riesiger Erfolg. Spannung, Teamgeist und großartige Atmosphäre – eine Tradition, die auf Fortsetzung hofft.',
    category: 'Turnier',
    highlight: false,
  },
];

const highlights = [
  'Helferfete – der große Abend für alle Helfer und Ehrenamtliche',
  'Vereinsfrühstück – gemütlich in den Sonntag starten',
  'Sommergaudi – Spaß, Grill und gute Laune',
  'Kniffelturnier – Würfelglück gesucht!',
  'Sportfest – der große Jahresabschluss',
];

const categoryColors = {
  Verein: 'bg-[#F5C200]/20 text-[#1a1a1a]',
  Jugend: 'bg-blue-100 text-blue-700',
  Fest: 'bg-green-100 text-green-700',
  Event: 'bg-purple-100 text-purple-700',
  Highlight: 'bg-red-100 text-red-700',
  Turnier: 'bg-orange-100 text-orange-700',
};

export default function Vereinsleben() {
  useEffect(() => {
    document.title = 'Vereinsleben | HSC Alswede – Events, Feste & Gemeinschaft';
  }, []);

  return (
    <div className="pt-16">
      {/* Hero */}
      <section
        className="relative py-20 lg:py-28 overflow-hidden"
        style={{
        // backgroundImage: 'url(https://images.pexels.com/photos/12074795/pexels-photo-12074795.jpeg?auto=compress&cs=tinysrgb&w=1400)',
          backgroundImage: "images/startseite/hero.jpg",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#1a1a1a]/65" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 text-[#F5C200] text-sm mb-4">
              <Link to="/" className="text-gray-300 hover:text-white transition-colors">Start</Link>
              <ChevronRight className="w-4 h-4" />
              <span>Vereinsleben</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Vereinsleben
            </h1>
            <p className="text-gray-300 max-w-xl">
              Events, Feste, Turniere und Gemeinschaft – beim HSC Alswede ist immer was los.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <FadeIn>
              <div>
                <div className="section-divider mb-4">
                  <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">Mehr als Sport</span>
                </div>
                <h2 className="text-3xl font-bold text-[#1a1a1a] mb-5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Gemeinschaft, die man fühlen kann
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Sport verbindet – und beim HSC Alswede ist das mehr als eine Phrase.
                  Wir feiern Erfolge zusammen, unterstützen uns bei Niederlagen
                  und halten zusammen, was zusammengehört.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Ob großes Sportfest, gemütliches Vereinsfrühstück oder spontanes
                  Kniffelturnier – bei uns gibt es immer einen Grund zusammenzukommen.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.15}>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { num: '450+', label: 'Mitglieder' },
                  { num: '10+', label: 'Jahres-Events' },
                  { num: '20+', label: 'Sportgruppen' },
                  { num: '80', label: 'Jahre Geschichte' },
                ].map((s, i) => (
                  <Card key={i} className="rounded-2xl border border-black/[0.08] bg-white p-4 shadow-[0_10px_30px_rgba(17,17,17,0.05)] text-center">
                    <div className="text-2xl font-bold text-[#F5C200] mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{s.num}</div>
                    <div className="text-xs text-gray-500">{s.label}</div>
                  </Card>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-12 lg:py-20 bg-[#FAFAF9]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="mb-10">
              <div className="section-divider mb-3">
                <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">Events & Highlights</span>
              </div>
              <h2 className="text-3xl font-bold text-[#1a1a1a]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Was uns bewegt
              </h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {events.map((event, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <Card
                  data-testid="event-card"
                  className={`event-card rounded-2xl border bg-white p-5 shadow-[0_10px_30px_rgba(17,17,17,0.05)] h-full relative ${
                    event.highlight ? 'border-[#F5C200]/40' : 'border-black/[0.08]'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <Badge className={`text-xs border-0 rounded-full ${categoryColors[event.category] || 'bg-gray-100 text-gray-600'}`}>
                      {event.category}
                    </Badge>
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <Calendar className="w-3 h-3" />
                      {event.date}
                    </div>
                  </div>
                  <h3 className="font-semibold text-[#1a1a1a] mb-2 leading-snug" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {event.title}
                  </h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{event.desc}</p>
                </Card>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Regular Events */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <div>
                <div className="section-divider mb-4">
                  <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">Traditionen</span>
                </div>
                <h2 className="text-3xl font-bold text-[#1a1a1a] mb-5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Unsere Jahresveranstaltungen
                </h2>
                <p className="text-gray-600 mb-6">
                  Manche Dinge gehören einfach zum HSC-Kalender. Diese Veranstaltungen haben Tradition –
                  und auf sie freuen sich unsere Mitglieder Jahr für Jahr.
                </p>
                <ul className="space-y-3">
                  {highlights.map((h, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#F5C200]/20 flex items-center justify-center flex-shrink-0">
                        <Star className="w-3 h-3 text-[#1a1a1a]" />
                      </div>
                      <span className="text-sm text-gray-700">{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
         </div>
        </div>
      </section>

      {/* Join CTA */}
      <section className="py-12 lg:py-16 bg-[#F5C200]">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <h2 className="text-3xl font-bold text-[#1a1a1a] mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Werde Teil unserer Vereinsfamilie!
            </h2>
            <p className="text-[#1a1a1a]/70 mb-6 max-w-xl mx-auto">
              Ob Sport oder Geselligkeit – beim HSC Alswede bist du herzlich willkommen. Komm einfach vorbei!
            </p>
            <Link to="/kontakt">
              <Button className="h-12 px-8 bg-[#1a1a1a] text-white hover:bg-[#333] font-semibold rounded-xl border-0 shadow-none transition-colors">
                Kontakt aufnehmen
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}